/**
 * useSwipeGesture Hook
 *
 * Detects swipe gestures (left, right, up, down) on touch-enabled devices.
 * Useful for mobile navigation, carousels, and dismissible panels.
 *
 * @example
 * ```tsx
 * const swipeHandlers = useSwipeGesture({
 *   onSwipeLeft: () => console.log('Swiped left'),
 *   onSwipeRight: () => console.log('Swiped right'),
 *   minSwipeDistance: 50,
 * });
 *
 * return <div {...swipeHandlers}>Swipe me!</div>;
 * ```
 */

import { useRef, TouchEvent } from 'react';

/**
 * Swipe direction
 */
export type SwipeDirection = 'left' | 'right' | 'up' | 'down';

/**
 * Swipe gesture configuration
 */
export interface SwipeConfig {
  /** Callback when swipe left is detected */
  onSwipeLeft?: () => void;
  /** Callback when swipe right is detected */
  onSwipeRight?: () => void;
  /** Callback when swipe up is detected */
  onSwipeUp?: () => void;
  /** Callback when swipe down is detected */
  onSwipeDown?: () => void;
  /** Callback with swipe direction and distance */
  onSwipe?: (direction: SwipeDirection, distance: number) => void;
  /** Minimum swipe distance in pixels (default: 50) */
  minSwipeDistance?: number;
  /** Maximum vertical deviation for horizontal swipe (default: 100) */
  maxVerticalDeviation?: number;
  /** Maximum horizontal deviation for vertical swipe (default: 100) */
  maxHorizontalDeviation?: number;
}

/**
 * Touch position interface
 */
interface TouchPosition {
  x: number;
  y: number;
  time: number;
}

/**
 * Hook return type
 */
export interface SwipeHandlers {
  onTouchStart: (e: TouchEvent) => void;
  onTouchEnd: (e: TouchEvent) => void;
}

/**
 * Hook to detect swipe gestures
 *
 * @param config - Swipe configuration options
 * @returns Touch event handlers to spread on target element
 */
export const useSwipeGesture = (config: SwipeConfig): SwipeHandlers => {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    onSwipe,
    minSwipeDistance = 50,
    maxVerticalDeviation = 100,
    maxHorizontalDeviation = 100,
  } = config;

  const touchStart = useRef<TouchPosition | null>(null);

  /**
   * Handle touch start event
   */
  const handleTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      touchStart.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };
    }
  };

  /**
   * Handle touch end event and detect swipe
   */
  const handleTouchEnd = (e: TouchEvent) => {
    if (!touchStart.current) {
      return;
    }

    const touch = e.changedTouches[0];
    if (!touch) {
      touchStart.current = null;
      return;
    }

    const touchEnd: TouchPosition = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };

    // Calculate swipe distance and direction
    const deltaX = touchEnd.x - touchStart.current.x;
    const deltaY = touchEnd.y - touchStart.current.y;
    const deltaTime = touchEnd.time - touchStart.current.time;

    // Calculate absolute distances
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    // Detect horizontal swipe
    if (absX > minSwipeDistance && absY < maxVerticalDeviation) {
      const direction: SwipeDirection = deltaX > 0 ? 'right' : 'left';

      // Call specific callback
      if (direction === 'left' && onSwipeLeft) {
        onSwipeLeft();
      } else if (direction === 'right' && onSwipeRight) {
        onSwipeRight();
      }

      // Call generic callback
      if (onSwipe) {
        onSwipe(direction, absX);
      }
    }
    // Detect vertical swipe
    else if (absY > minSwipeDistance && absX < maxHorizontalDeviation) {
      const direction: SwipeDirection = deltaY > 0 ? 'down' : 'up';

      // Call specific callback
      if (direction === 'up' && onSwipeUp) {
        onSwipeUp();
      } else if (direction === 'down' && onSwipeDown) {
        onSwipeDown();
      }

      // Call generic callback
      if (onSwipe) {
        onSwipe(direction, absY);
      }
    }

    // Reset touch start
    touchStart.current = null;
  };

  return {
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
  };
};
